"use client";

/**
 * Shared “magic torch” stack from `magic-cards.tsx` (authoritative):
 * section-scoped torch spotlight, per-surface glow CSS vars, edge gradient,
 * subtle Y lift + shadow on hover, optional GSAP particles, tilt & magnetism.
 *
 * Usage: wrap a region with `MagicTorch`, then wrap each interactive tile with
 * `MagicTorchSurface`. Do not nest one `MagicTorch` inside another (single global spotlight).
 */

import { gsap } from "gsap";
import { useReducedMotion } from "motion/react";
import type { Ref } from "react";
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { cn } from "../../lib";
import {
	glassBorderRingHostClassNames,
	glassBorderRingStylesheet,
} from "../custom/glass-border-ring";

// --- Defaults (aligned with `magic-cards.tsx`) --------------------------------

export const MAGIC_TORCH_DEFAULT_PARTICLE_COUNT = 12;
export const MAGIC_TORCH_DEFAULT_SPOTLIGHT_RADIUS = 320;
/** Comma-separated RGB — stronger on light surfaces. */
export const MAGIC_TORCH_DEFAULT_GLOW_COLOR = "100, 0, 200";
/** Comma-separated RGB — brighter accent on dark surfaces. */
export const MAGIC_TORCH_DEFAULT_GLOW_COLOR_DARK = "132, 0, 255";
export const MAGIC_TORCH_DISABLED_GLOW_COLOR = "210, 210, 210";
export const MAGIC_TORCH_DISABLED_GLOW_COLOR_DARK = "120, 120, 120";

const MOBILE_BREAKPOINT = 768;

const TORCH_SURFACE_SELECTOR = "[data-magic-torch-surface]";

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
	if (!ref) {
		return;
	}
	if (typeof ref === "function") {
		ref(value);
	} else {
		(ref as React.MutableRefObject<T | null>).current = value;
	}
}

function calculateSpotlightValues(radius: number) {
	return {
		proximity: radius * 0.5,
		fadeDistance: radius * 0.75,
	};
}

function updateCardGlowProperties(
	card: HTMLElement,
	mouseX: number,
	mouseY: number,
	glow: number,
	radius: number,
) {
	const rect = card.getBoundingClientRect();
	const relativeX = ((mouseX - rect.left) / rect.width) * 100;
	const relativeY = ((mouseY - rect.top) / rect.height) * 100;

	card.style.setProperty("--glow-x", `${relativeX}%`);
	card.style.setProperty("--glow-y", `${relativeY}%`);
	card.style.setProperty("--glow-intensity", glow.toString());
	card.style.setProperty("--glow-radius", `${radius}px`);
}

function createParticleElement(
	x: number,
	y: number,
	color: string,
): HTMLDivElement {
	const el = document.createElement("div");
	el.className = "magic-torch-particle";
	el.style.cssText = `
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(${color}, 1);
    box-shadow: 0 0 6px rgba(${color}, 0.6);
    pointer-events: none;
    z-index: 100;
    left: ${x}px;
    top: ${y}px;
  `;
	return el;
}

function useMobileDetection(): boolean {
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		const checkMobile = () =>
			setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);
	return isMobile;
}

/** Matches `next-themes` / Tailwind: `class="dark"` on `documentElement`. */
function useDocumentDarkClass(): boolean {
	const [isDark, setIsDark] = useState(false);

	useEffect(() => {
		if (typeof document === "undefined") {
			return;
		}
		const root = document.documentElement;
		const read = () => {
			setIsDark(root.classList.contains("dark"));
		};
		read();
		const obs = new MutationObserver(read);
		obs.observe(root, { attributes: true, attributeFilter: ["class"] });
		return () => {
			obs.disconnect();
		};
	}, []);

	return isDark;
}

// --- Context ----------------------------------------------------------------

export interface MagicTorchDefaults {
	enableParticles: boolean;
	particleCount: number;
	enableTilt: boolean;
	enableMagnetism: boolean;
	enableBorderGlow: boolean;
}

export interface MagicTorchContextValue {
	/** Theme class on `documentElement` at torch root — avoids per-surface observers. */
	isDark: boolean;
	resolvedGlowRgb: string;
	spotlightRadius: number;
	shouldDisableHeavyEffects: boolean;
	defaults: MagicTorchDefaults;
}

const MagicTorchContext = createContext<MagicTorchContextValue | null>(null);

export function useMagicTorch(): MagicTorchContextValue {
	const ctx = useContext(MagicTorchContext);
	if (!ctx) {
		throw new Error("useMagicTorch must be used within <MagicTorch>");
	}
	return ctx;
}

// --- Global spotlight (ported from `GlobalSpotlight` in magic-cards) --------

interface MagicTorchGlobalSpotlightProps {
	rootRef: React.RefObject<HTMLElement | null>;
	disableAnimations?: boolean;
	enabled?: boolean;
	spotlightRadius?: number;
	glowColor?: string;
	/** Elements that receive `--glow-*` updates (default matches `MagicTorchSurface`). */
	targetSelector?: string;
	spotlightElementClassName?: string;
}

function MagicTorchGlobalSpotlight({
	rootRef,
	disableAnimations = false,
	enabled = true,
	spotlightRadius = MAGIC_TORCH_DEFAULT_SPOTLIGHT_RADIUS,
	glowColor = MAGIC_TORCH_DEFAULT_GLOW_COLOR_DARK,
	targetSelector = TORCH_SURFACE_SELECTOR,
	spotlightElementClassName = "magic-torch-global-spotlight",
}: MagicTorchGlobalSpotlightProps) {
	const spotlightRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (disableAnimations || !rootRef.current || !enabled) {
			return;
		}

		const spotlight = document.createElement("div");
		spotlight.className = spotlightElementClassName;
		spotlight.style.cssText = `
      position: fixed;
      width: 800px;
      height: 800px;
      border-radius: 50%;
      pointer-events: none;
      background: radial-gradient(circle,
        rgba(${glowColor}, 0.15) 0%,
        rgba(${glowColor}, 0.08) 15%,
        rgba(${glowColor}, 0.04) 25%,
        rgba(${glowColor}, 0.02) 40%,
        rgba(${glowColor}, 0.01) 65%,
        transparent 70%
      );
      z-index: 200;
      opacity: 0;
      transform: translate(-50%, -50%);
      mix-blend-mode: screen;
    `;
		document.body.appendChild(spotlight);
		spotlightRef.current = spotlight;

		const handleMouseMove = (e: MouseEvent) => {
			if (!spotlightRef.current || !rootRef.current) {
				return;
			}

			const rect = rootRef.current.getBoundingClientRect();
			const mouseInside =
				e.clientX >= rect.left &&
				e.clientX <= rect.right &&
				e.clientY >= rect.top &&
				e.clientY <= rect.bottom;

			const surfaces = rootRef.current.querySelectorAll(targetSelector);

			if (!mouseInside) {
				gsap.to(spotlightRef.current, {
					opacity: 0,
					duration: 0.3,
					ease: "power2.out",
				});
				surfaces.forEach((node) => {
					(node as HTMLElement).style.setProperty(
						"--glow-intensity",
						"0",
					);
				});
				return;
			}

			const { proximity, fadeDistance } =
				calculateSpotlightValues(spotlightRadius);
			let minDistance = Number.POSITIVE_INFINITY;

			surfaces.forEach((node) => {
				const el = node as HTMLElement;
				const cr = el.getBoundingClientRect();
				const centerX = cr.left + cr.width / 2;
				const centerY = cr.top + cr.height / 2;
				const distance =
					Math.hypot(e.clientX - centerX, e.clientY - centerY) -
					Math.max(cr.width, cr.height) / 2;
				const effective = Math.max(0, distance);
				minDistance = Math.min(minDistance, effective);

				let glow = 0;
				if (effective <= proximity) {
					glow = 1;
				} else if (effective <= fadeDistance) {
					glow =
						(fadeDistance - effective) / (fadeDistance - proximity);
				}
				updateCardGlowProperties(
					el,
					e.clientX,
					e.clientY,
					glow,
					spotlightRadius,
				);
			});

			gsap.to(spotlightRef.current, {
				left: e.clientX,
				top: e.clientY,
				duration: 0.1,
				ease: "power2.out",
			});

			const targetOpacity =
				minDistance <= proximity
					? 0.8
					: minDistance <= fadeDistance
						? ((fadeDistance - minDistance) /
								(fadeDistance - proximity)) *
							0.8
						: 0;

			gsap.to(spotlightRef.current, {
				opacity: targetOpacity,
				duration: targetOpacity > 0 ? 0.2 : 0.5,
				ease: "power2.out",
			});
		};

		const handleMouseLeave = () => {
			rootRef.current
				?.querySelectorAll(targetSelector)
				.forEach((node) => {
					(node as HTMLElement).style.setProperty(
						"--glow-intensity",
						"0",
					);
				});
			if (spotlightRef.current) {
				gsap.to(spotlightRef.current, {
					opacity: 0,
					duration: 0.3,
					ease: "power2.out",
				});
			}
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseleave", handleMouseLeave);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseleave", handleMouseLeave);
			spotlightRef.current?.parentNode?.removeChild(spotlightRef.current);
		};
	}, [
		rootRef,
		disableAnimations,
		enabled,
		spotlightRadius,
		glowColor,
		targetSelector,
		spotlightElementClassName,
	]);

	return null;
}

// --- Interactive surface (particles + tilt + magnetism from MagicParticleCard)

interface MagicTorchInteractiveLayerProps {
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	disableAnimations?: boolean;
	particleCount?: number;
	glowColor?: string;
	enableTilt?: boolean;
	enableMagnetism?: boolean;
	/** GSAP radial ripple on click (Magic Bento parity). */
	clickEffect?: boolean;
	/** Also set `data-bento-card-disabled` for legacy bento CSS. */
	compatibilityBentoCardDisabled?: boolean;
	dimmed?: boolean;
	surfaceActive?: boolean;
	disabled?: boolean;
	onActivate?: () => void;
}

function MagicTorchInteractiveLayer({
	children,
	className = "",
	disableAnimations = false,
	style,
	particleCount = MAGIC_TORCH_DEFAULT_PARTICLE_COUNT,
	glowColor = MAGIC_TORCH_DEFAULT_GLOW_COLOR_DARK,
	enableTilt = true,
	enableMagnetism = false,
	clickEffect = false,
	compatibilityBentoCardDisabled = false,
	dimmed = false,
	surfaceActive = false,
	disabled = false,
	onActivate,
}: MagicTorchInteractiveLayerProps) {
	const disabledDataset =
		disabled && compatibilityBentoCardDisabled
			? ({
					"data-magic-torch-disabled": "",
					"data-bento-card-disabled": "",
				} as const)
			: disabled
				? ({ "data-magic-torch-disabled": "" } as const)
				: {};

	const cardRef = useRef<HTMLDivElement>(null);
	const onActivateRef = useRef(onActivate);
	onActivateRef.current = onActivate;
	const disabledRef = useRef(disabled);
	disabledRef.current = disabled;
	const particlesRef = useRef<HTMLDivElement[]>([]);
	const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	const isHoveredRef = useRef(false);
	const memoizedParticles = useRef<HTMLDivElement[]>([]);
	const particlesInitialized = useRef(false);
	const magnetismAnimationRef = useRef<gsap.core.Tween | null>(null);

	const initializeParticles = useCallback(() => {
		if (particlesInitialized.current || !cardRef.current) {
			return;
		}
		const { width, height } = cardRef.current.getBoundingClientRect();
		memoizedParticles.current = Array.from({ length: particleCount }, () =>
			createParticleElement(
				Math.random() * width,
				Math.random() * height,
				glowColor,
			),
		);
		particlesInitialized.current = true;
	}, [particleCount, glowColor]);

	const clearAllParticles = useCallback(() => {
		timeoutsRef.current.forEach(clearTimeout);
		timeoutsRef.current = [];
		magnetismAnimationRef.current?.kill();

		particlesRef.current.forEach((particle) => {
			gsap.to(particle, {
				scale: 0,
				opacity: 0,
				duration: 0.3,
				ease: "back.in(1.7)",
				onComplete: () => {
					particle.parentNode?.removeChild(particle);
				},
			});
		});
		particlesRef.current = [];
	}, []);

	const animateParticles = useCallback(() => {
		if (!cardRef.current || !isHoveredRef.current) {
			return;
		}
		if (!particlesInitialized.current) {
			initializeParticles();
		}
		memoizedParticles.current.forEach((particle, index) => {
			const timeoutId = setTimeout(() => {
				if (!isHoveredRef.current || !cardRef.current) {
					return;
				}
				const clone = particle.cloneNode(true) as HTMLDivElement;
				cardRef.current.appendChild(clone);
				particlesRef.current.push(clone);

				gsap.fromTo(
					clone,
					{ scale: 0, opacity: 0 },
					{
						scale: 1,
						opacity: 1,
						duration: 0.3,
						ease: "back.out(1.7)",
					},
				);

				gsap.to(clone, {
					x: (Math.random() - 0.5) * 90,
					y: (Math.random() - 0.5) * 90,
					rotation: Math.random() * 360,
					duration: 2 + Math.random() * 2,
					ease: "none",
					repeat: -1,
					yoyo: true,
				});

				gsap.to(clone, {
					opacity: 0.28,
					duration: 1.5,
					ease: "power2.inOut",
					repeat: -1,
					yoyo: true,
				});
			}, index * 90);
			timeoutsRef.current.push(timeoutId);
		});
	}, [initializeParticles]);

	useEffect(() => {
		if (disableAnimations || !cardRef.current || disabled) {
			return;
		}
		const el = cardRef.current;

		const handleMouseEnter = () => {
			if (disabledRef.current) {
				return;
			}
			isHoveredRef.current = true;
			animateParticles();
			if (enableTilt) {
				gsap.to(el, {
					rotateX: 5,
					rotateY: 5,
					duration: 0.25,
					ease: "power2.out",
					transformPerspective: 1000,
				});
			}
		};

		const handleMouseLeave = () => {
			isHoveredRef.current = false;
			clearAllParticles();
			if (enableTilt) {
				gsap.to(el, {
					rotateX: 0,
					rotateY: 0,
					duration: 0.25,
					ease: "power2.out",
				});
			}
			if (enableMagnetism) {
				gsap.to(el, { x: 0, y: 0, duration: 0.25, ease: "power2.out" });
			}
		};

		const handleMouseMove = (e: MouseEvent) => {
			if (disabledRef.current) {
				return;
			}
			if (!enableTilt && !enableMagnetism) {
				return;
			}
			const rect = el.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;
			const cx = rect.width / 2;
			const cy = rect.height / 2;

			if (enableTilt) {
				const rotateX = ((y - cy) / cy) * -10;
				const rotateY = ((x - cx) / cx) * 10;
				gsap.to(el, {
					rotateX,
					rotateY,
					duration: 0.1,
					ease: "power2.out",
					transformPerspective: 1000,
				});
			}

			if (enableMagnetism) {
				const mx = (x - cx) * 0.045;
				const my = (y - cy) * 0.045;
				magnetismAnimationRef.current = gsap.to(el, {
					x: mx,
					y: my,
					duration: 0.25,
					ease: "power2.out",
				});
			}
		};

		const handleClick = (e: MouseEvent) => {
			if (disabledRef.current) {
				return;
			}
			// Native listeners fire before React root stopPropagation — ignore
			// clicks that originated on a Magic Bento hover CTA (+NEW).
			const target = e.target;
			if (
				target instanceof Element &&
				target.closest("[data-bento-hover-cta]")
			) {
				return;
			}
			onActivateRef.current?.();
			if (!clickEffect) {
				return;
			}

			const rect = el.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;
			const maxDistance = Math.max(
				Math.hypot(x, y),
				Math.hypot(x - rect.width, y),
				Math.hypot(x, y - rect.height),
				Math.hypot(x - rect.width, y - rect.height),
			);

			const ripple = document.createElement("div");
			ripple.style.cssText = `
        position: absolute;
        width: ${maxDistance * 2}px;
        height: ${maxDistance * 2}px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(${glowColor}, 0.4) 0%, rgba(${glowColor}, 0.2) 30%, transparent 70%);
        left: ${x - maxDistance}px;
        top: ${y - maxDistance}px;
        pointer-events: none;
        z-index: 1000;
      `;

			el.appendChild(ripple);

			gsap.fromTo(
				ripple,
				{
					scale: 0,
					opacity: 1,
				},
				{
					scale: 1,
					opacity: 0,
					duration: 0.8,
					ease: "power2.out",
					onComplete: () => ripple.remove(),
				},
			);
		};

		el.addEventListener("mouseenter", handleMouseEnter);
		el.addEventListener("mouseleave", handleMouseLeave);
		el.addEventListener("mousemove", handleMouseMove);
		el.addEventListener("click", handleClick);

		return () => {
			isHoveredRef.current = false;
			el.removeEventListener("mouseenter", handleMouseEnter);
			el.removeEventListener("mouseleave", handleMouseLeave);
			el.removeEventListener("mousemove", handleMouseMove);
			el.removeEventListener("click", handleClick);
			clearAllParticles();
		};
	}, [
		animateParticles,
		clearAllParticles,
		clickEffect,
		disableAnimations,
		disabled,
		enableMagnetism,
		enableTilt,
		glowColor,
	]);

	return (
		<div
			ref={cardRef}
			data-magic-torch-surface=""
			{...(surfaceActive ? { "data-active": "" } : {})}
			{...disabledDataset}
			className={cn(
				className,
				"relative overflow-visible",
				dimmed &&
					"pointer-events-none opacity-40 saturate-0 after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[var(--magic-torch-radius,24px)] after:bg-white/20 after:backdrop-blur-sm dark:after:bg-black/20",
				disabled && "cursor-not-allowed",
			)}
			style={{ ...style, position: "relative", overflow: "visible" }}
			aria-disabled={disabled ? true : undefined}
		>
			{children}
		</div>
	);
}

// --- Public components -------------------------------------------------------

export interface MagicTorchProps {
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	/** When false, skips the fixed-position cursor spotlight. */
	enableSpotlight?: boolean;
	/**
	 * When true, disables particles, spotlight listeners, and tilt/magnetism on surfaces.
	 * Also respected when `respectReducedMotion` is true and the user prefers reduced motion.
	 */
	disableAnimations?: boolean;
	/**
	 * When true (default), viewport widths ≤768px skip heavy effects (matches `MagicCards`).
	 */
	treatMobileAsNoMotion?: boolean;
	/**
	 * When true, `prefers-reduced-motion: reduce` skips heavy effects (spotlight, particles, tilt).
	 * Default false to match `MagicCards`; opt in for stricter a11y.
	 */
	respectReducedMotion?: boolean;
	glowColor?: string;
	glowColorDark?: string;
	spotlightRadius?: number;
	/** Radius token for clip-path / ::after (default 24px — same as magic cards). */
	surfaceBorderRadius?: string;
	/** Default per-surface toggles; each `MagicTorchSurface` may override. */
	defaultEnableParticles?: boolean;
	defaultParticleCount?: number;
	defaultEnableTilt?: boolean;
	defaultEnableMagnetism?: boolean;
	defaultEnableBorderGlow?: boolean;
	/** Query relative to this root for glow updates (must match surfaces you want lit). */
	torchTargetSelector?: string;
	/** Class on the portal spotlight element (avoid collisions if multiple torch stacks exist). */
	spotlightElementClassName?: string;
	/**
	 * Optional ref to the same node as the torch root (measurement host, `ResizeObserver`, etc.).
	 */
	torchRootRef?: Ref<HTMLDivElement | null>;
	/** Merged onto the torch root element (e.g. `aria-busy` while layout measures). */
	torchRootAttributes?: React.HTMLAttributes<HTMLDivElement>;
}

export function MagicTorch({
	children,
	className,
	style,
	enableSpotlight = true,
	disableAnimations = false,
	treatMobileAsNoMotion = true,
	respectReducedMotion = false,
	glowColor = MAGIC_TORCH_DEFAULT_GLOW_COLOR,
	glowColorDark = MAGIC_TORCH_DEFAULT_GLOW_COLOR_DARK,
	spotlightRadius = MAGIC_TORCH_DEFAULT_SPOTLIGHT_RADIUS,
	surfaceBorderRadius = "24px",
	defaultEnableParticles = true,
	defaultParticleCount = MAGIC_TORCH_DEFAULT_PARTICLE_COUNT,
	defaultEnableTilt = false,
	defaultEnableMagnetism = false,
	defaultEnableBorderGlow = true,
	torchTargetSelector = TORCH_SURFACE_SELECTOR,
	spotlightElementClassName = "magic-torch-global-spotlight",
	torchRootRef,
	torchRootAttributes,
}: MagicTorchProps) {
	const rootRef = useRef<HTMLDivElement>(null);
	const setTorchRoot = useCallback(
		(el: HTMLDivElement | null) => {
			rootRef.current = el;
			assignRef(torchRootRef, el);
		},
		[torchRootRef],
	);
	const isMobile = useMobileDetection();
	const isDark = useDocumentDarkClass();
	const prefersReducedMotion = useReducedMotion();

	const resolvedGlowRgb = isDark ? glowColorDark : glowColor;

	const shouldDisableHeavyEffects = Boolean(
		disableAnimations ||
			(treatMobileAsNoMotion && isMobile) ||
			(respectReducedMotion && prefersReducedMotion),
	);

	const contextValue = useMemo<MagicTorchContextValue>(
		() => ({
			isDark,
			resolvedGlowRgb,
			spotlightRadius,
			shouldDisableHeavyEffects,
			defaults: {
				enableParticles: defaultEnableParticles,
				particleCount: defaultParticleCount,
				enableTilt: defaultEnableTilt,
				enableMagnetism: defaultEnableMagnetism,
				enableBorderGlow: defaultEnableBorderGlow,
			},
		}),
		[
			isDark,
			resolvedGlowRgb,
			spotlightRadius,
			shouldDisableHeavyEffects,
			defaultEnableParticles,
			defaultParticleCount,
			defaultEnableTilt,
			defaultEnableMagnetism,
			defaultEnableBorderGlow,
		],
	);

	const cssBlock = useMemo(
		() => `
          .magic-torch-root {
            --glow-x: 50%;
            --glow-y: 50%;
            --glow-intensity: 0;
            --glow-radius: 240px;
            --magic-torch-radius: ${surfaceBorderRadius};
            --card-glow-color-rgb: ${resolvedGlowRgb};
          }

          .magic-torch-root .magic-torch-surface {
            overflow: visible;
            transform: translate3d(0,0,0);
            transition:
              transform 0.28s ease,
              box-shadow 0.28s ease,
              border-color 0.28s ease;
            will-change: transform, box-shadow;
          }

          .magic-torch-root .magic-torch-surface:not([data-magic-torch-disabled]):hover {
            transform: translate3d(0, -2px, 0);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          }

          .magic-torch-root .magic-torch-surface[data-magic-torch-disabled] {
            cursor: not-allowed;
          }

          .magic-torch-root .magic-torch-surface[data-active]:not([data-magic-torch-disabled]) {
            transform: translate3d(0, -2px, 0);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          }

          .magic-torch-root .magic-torch-surface.magic-torch-surface--border-glow {
            border-radius: var(--magic-torch-radius);
            isolation: isolate;
          }

          ${glassBorderRingStylesheet(".magic-torch-root")}

          .magic-torch-root .glass-border-ring-host::before {
            border-radius: var(--magic-torch-radius);
            -webkit-clip-path: inset(0 round var(--magic-torch-radius));
            clip-path: inset(0 round var(--magic-torch-radius));
          }

          .magic-torch-root .magic-torch-surface.magic-torch-surface--border-glow::after {
            content: '';
            position: absolute;
            inset: 0;
            box-sizing: border-box;
            padding: 6px;
            border-radius: var(--magic-torch-radius);
            -webkit-clip-path: inset(0 round var(--magic-torch-radius));
            clip-path: inset(0 round var(--magic-torch-radius));
            background: radial-gradient(var(--glow-radius) circle at var(--glow-x) var(--glow-y),
              rgba(var(--card-glow-color-rgb, ${resolvedGlowRgb}), calc(var(--glow-intensity) * 0.8)) 0%,
              rgba(var(--card-glow-color-rgb, ${resolvedGlowRgb}), calc(var(--glow-intensity) * 0.4)) 30%,
              transparent 60%);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
            z-index: 2;
            opacity: 1;
          }

          .magic-torch-root .magic-torch-surface.magic-torch-surface--border-glow > * {
            position: relative;
            z-index: 3;
          }

          /* Notify dots must stay absolute — torch rule above forces relative on direct children. */
          .magic-torch-root .magic-torch-surface.magic-torch-surface--border-glow > [data-bento-notify-dot] {
            position: absolute;
            z-index: 40;
          }

          .magic-torch-root .magic-torch-surface__inner {
            position: relative;
            z-index: 2;
          }

          .magic-torch-particle::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: rgba(var(--card-glow-color-rgb, ${resolvedGlowRgb}), 0.2);
            border-radius: 50%;
            z-index: -1;
          }
        `,
		[resolvedGlowRgb, surfaceBorderRadius],
	);

	return (
		<MagicTorchContext.Provider value={contextValue}>
			<style>{cssBlock}</style>
			{enableSpotlight && !shouldDisableHeavyEffects ? (
				<MagicTorchGlobalSpotlight
					rootRef={rootRef}
					disableAnimations={shouldDisableHeavyEffects}
					enabled={enableSpotlight}
					spotlightRadius={spotlightRadius}
					glowColor={resolvedGlowRgb}
					targetSelector={torchTargetSelector}
					spotlightElementClassName={spotlightElementClassName}
				/>
			) : null}
			<div
				ref={setTorchRoot}
				className={cn("magic-torch-root relative", className)}
				style={{
					...style,
					["--magic-torch-radius" as string]: surfaceBorderRadius,
				}}
				{...torchRootAttributes}
			>
				{children}
			</div>
		</MagicTorchContext.Provider>
	);
}

export interface MagicTorchSurfaceProps {
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	disabled?: boolean;
	/** Sets `data-active` for persisted “selected” chrome (e.g. dim-unselected flows). */
	active?: boolean;
	dimmed?: boolean;
	/**
	 * Comma-separated RGB override for this surface’s `--card-glow-color-rgb`.
	 * When `disabled` is true, defaults to neutral greys unless you pass explicit RGB.
	 */
	glowColorRgb?: string;
	enableParticles?: boolean;
	particleCount?: number;
	enableBorderGlow?: boolean;
	/**
	 * Ring-only glass band via {@link glassBorderRingHostClassNames}. Opt-in for
	 * bento/card tiles; leave off when the surface already wraps {@link GlassBorder}.
	 */
	enableGlassBorderRing?: boolean;
	enableTilt?: boolean;
	enableMagnetism?: boolean;
	onClick?: () => void;
	/** Alias for `onClick` — matches `MagicCards` naming. */
	onActivate?: () => void;
	/** When set, GSAP particle colour uses this RGB instead of the surface edge glow. */
	particleGlowRgb?: string;
	/** Radial ripple on click when particles layer is active (Magic Bento). */
	clickEffect?: boolean;
	/** Set `data-bento-card-disabled` alongside torch disabled attr for Magic Bento CSS. */
	compatibilityBentoCardDisabled?: boolean;
	/** When true, renders a single child wrapper with z-index above the torch edge (::after). */
	wrapChildrenInSurfaceInner?: boolean;
}

export function MagicTorchSurface({
	children,
	className,
	style,
	disabled = false,
	active = false,
	dimmed = false,
	glowColorRgb,
	enableParticles: enableParticlesProp,
	particleCount: particleCountProp,
	enableBorderGlow: enableBorderGlowProp,
	enableGlassBorderRing: enableGlassBorderRingProp,
	enableTilt: enableTiltProp,
	enableMagnetism: enableMagnetismProp,
	onClick,
	onActivate,
	particleGlowRgb,
	clickEffect = false,
	compatibilityBentoCardDisabled = false,
	wrapChildrenInSurfaceInner = true,
}: MagicTorchSurfaceProps) {
	const ctx = useMagicTorch();

	const enableParticles = enableParticlesProp ?? ctx.defaults.enableParticles;
	const particleCount = particleCountProp ?? ctx.defaults.particleCount;
	const enableTilt = enableTiltProp ?? ctx.defaults.enableTilt;
	const enableMagnetism = enableMagnetismProp ?? ctx.defaults.enableMagnetism;
	const enableBorderGlow =
		enableBorderGlowProp ?? ctx.defaults.enableBorderGlow;
	const enableGlassBorderRing = enableGlassBorderRingProp ?? false;

	const resolvedCardGlow = disabled
		? (glowColorRgb ??
			(ctx.isDark
				? MAGIC_TORCH_DISABLED_GLOW_COLOR_DARK
				: MAGIC_TORCH_DISABLED_GLOW_COLOR))
		: (glowColorRgb ?? ctx.resolvedGlowRgb);

	const surfaceStyle = useMemo(
		() =>
			({
				...style,
				"--glow-x": "50%",
				"--glow-y": "50%",
				"--glow-intensity": "0",
				"--glow-radius": `${ctx.spotlightRadius}px`,
				"--card-glow-color-rgb": resolvedCardGlow,
			}) as React.CSSProperties,
		[style, ctx.spotlightRadius, resolvedCardGlow],
	);

	const baseSurfaceClass = cn(
		"magic-torch-surface group",
		enableBorderGlow && "magic-torch-surface--border-glow",
		enableBorderGlow &&
			enableGlassBorderRing &&
			glassBorderRingHostClassNames({ tone: "system", disabled }),
		className,
	);

	const activate = onActivate ?? onClick;
	const particleColor = particleGlowRgb ?? resolvedCardGlow;

	const inner = wrapChildrenInSurfaceInner ? (
		<div className="magic-torch-surface__inner h-full w-full min-h-0 min-w-0">
			{children}
		</div>
	) : (
		children
	);

	const useParticleLayer =
		enableParticles && !ctx.shouldDisableHeavyEffects && !disabled;

	const disabledDatasetOuter =
		disabled && compatibilityBentoCardDisabled
			? ({
					"data-magic-torch-disabled": "",
					"data-bento-card-disabled": "",
				} as const)
			: disabled
				? ({ "data-magic-torch-disabled": "" } as const)
				: {};

	if (useParticleLayer) {
		return (
			<MagicTorchInteractiveLayer
				className={baseSurfaceClass}
				style={surfaceStyle}
				disableAnimations={ctx.shouldDisableHeavyEffects}
				particleCount={particleCount}
				glowColor={particleColor}
				enableTilt={enableTilt}
				enableMagnetism={enableMagnetism}
				clickEffect={clickEffect}
				compatibilityBentoCardDisabled={compatibilityBentoCardDisabled}
				dimmed={dimmed}
				surfaceActive={active}
				disabled={disabled}
				onActivate={activate}
			>
				{inner}
			</MagicTorchInteractiveLayer>
		);
	}

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: Styled card shell; `role="button"` + handlers only when `onActivate` / `onClick` is set (mirrors `MagicCards` non-particle tile).
		<div
			data-magic-torch-surface=""
			{...(active ? { "data-active": "" } : {})}
			{...disabledDatasetOuter}
			className={cn(
				baseSurfaceClass,
				dimmed &&
					"pointer-events-none opacity-40 saturate-0 after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[var(--magic-torch-radius,24px)] after:bg-white/20 after:backdrop-blur-sm dark:after:bg-black/20",
				disabled && "cursor-not-allowed",
			)}
			style={surfaceStyle}
			aria-disabled={disabled ? true : undefined}
			onClick={disabled || !activate ? undefined : activate}
			onKeyDown={
				disabled || !activate
					? undefined
					: (e) => {
							if (e.target !== e.currentTarget) {
								return;
							}
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								activate();
							}
						}
			}
			role={activate ? "button" : undefined}
			tabIndex={activate ? (disabled ? -1 : 0) : undefined}
		>
			{inner}
		</div>
	);
}
